// Lógica pura de dedup do `play()` no LivePlayerProvider.
// Extraído pra testar isoladamente — regra propensa a regressão (já
// quebrou uma vez no PR #96, corrigida no PR #109).

import type { LivePlayerState } from './types'

/** Ação que o `play()` deve tomar com base no estado atual. */
export type PlayAction =
  /** Nada a fazer — player já está tocando exatamente esse channelId. */
  | { type: 'noop' }
  /** Carregar de fato (chama getManualSource + doLoad). */
  | { type: 'load' }

// NOTA (perf/tv-zapping): existia um terceiro caminho, 'resume-from-suspend' —
// player vivo + mesmo canal + status idle => só marcar 'loading' e deixar o
// `attachTo` dar play() no <video>, sem recarregar. Ele fazia sentido quando o
// `doSuspend` só PAUSAVA o vídeo (o mpegts seguia baixando por baixo: o stream
// zumbi que congelava a UI por 5,5s). Agora que o suspend PARA o pipeline, não
// existe mais "é só dar play" — é preciso re-armar a carga.
//
// Tentei preservar o atalho com um `player.resume()` (mpegts.unload/load na
// mesma MediaSource). Nos testes passou; na LG real ele NÃO re-armou o pipeline
// — sem GET novo, sem erro, sem log — e a tela ficava preta pra sempre. Caminho
// que não dá pra provar no device não entra em produção: o retorno agora usa a
// MESMA carga completa do zap (medida: ~600ms até o loadstart), que é o caminho
// que o GC já exercitava e que está provado na TV. O custo é um /stream/sign a
// mais no retorno — folgado dentro do que economizamos matando o HEAD (240-434ms
// por zap).

interface DecideInput {
  /** True se há `MediaPlayer` instanciado (playerRef.current não-nulo). */
  hasPlayer: boolean
  current: Pick<LivePlayerState, 'channelId' | 'status' | 'group'>
  requestedChannelId: string
  requestedGroupKey: string
}

/**
 * Regra de dedup do `play(channelId, group)`.
 *
 * INVARIANTE CRÍTICO: dedup compara por `channelId`, NÃO por `group.key`.
 *
 * Histórico: PR #96 mudou pra `group.key` — quebrou troca de canal dentro
 * do mesmo grupo lógico e retentativas após auto-switch ter degradado a
 * source. Sintoma: "clico no canal e abre o que já tava". Corrigido em
 * PR #109. NÃO MUDE essa regra sem entender o histórico completo.
 *
 * Pra trocar manualmente entre sources do mesmo grupo, usar selectSource()
 * (API separada, idempotente, persiste manual em IDB).
 */
export function decidePlayAction(input: DecideInput): PlayAction {
  const { hasPlayer, current, requestedChannelId } = input

  // Sem player instanciado: precisa carregar do zero.
  if (!hasPlayer) return { type: 'load' }

  const sameChannel = current.channelId === requestedChannelId

  // Mesmo channelId TOCANDO (ou já carregando): no-op. Esta é a única guarda
  // que sobrou — e é a que segura o dedup de zapping.
  if (sameChannel && current.status !== 'idle' && current.status !== 'error') {
    return { type: 'noop' }
  }

  // Qualquer outro caso — inclusive "voltei pra live e o player está suspenso"
  // (status idle) — carrega. Ver a nota sobre 'resume-from-suspend' acima:
  // status idle NÃO significa mais "é só dar play", significa "o pipeline foi
  // parado". Deixar de carregar aqui = tela preta.
  return { type: 'load' }
}
